import { useRef, useState } from "react";
import { toast } from "sonner";
import api, { mediaSrc, apiError } from "../api";
import { Upload, X, Loader2, Paperclip, FileText } from "lucide-react";

export function ImageUpload({ value, onChange, height = "h-36", className = "" }) {
  const ref = useRef();
  const [busy, setBusy] = useState(false);

  const onFile = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/media/upload", fd);
      onChange(data.url);
      toast.success("Image uploaded");
    } catch (err) { toast.error(apiError(err, "Upload failed")); }
    finally { setBusy(false); e.target.value = ""; }
  };

  return (
    <div className={className}>
      <input type="file" accept="image/*" ref={ref} onChange={onFile} className="hidden" data-testid="image-upload-input" />
      {value ? (
        <div className={`relative group w-full ${height}`}>
          <img src={mediaSrc(value)} alt="" className={`w-full ${height} object-cover rounded-lg border border-white/10`} />
          <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2 rounded-lg">
            <button type="button" className="btn-secondary !py-1.5 text-sm" onClick={() => ref.current.click()}>Replace</button>
            <button type="button" className="btn-danger !py-1.5 text-sm" onClick={() => onChange("")}><X size={14} /> Remove</button>
          </div>
        </div>
      ) : (
        <button type="button" onClick={() => ref.current.click()} disabled={busy}
          className={`w-full ${height} rounded-lg border-2 border-dashed border-white/15 hover:border-[#F26522]/60 flex flex-col items-center justify-center gap-2 text-slate-400 transition-colors`}
          data-testid="image-upload-btn">
          {busy ? <Loader2 className="animate-spin" /> : <Upload size={22} />}
          <span className="text-sm">{busy ? "Uploading..." : "Click to upload image"}</span>
        </button>
      )}
    </div>
  );
}

export function AvatarUpload({ value, name, onChange }) {
  const ref = useRef();
  const [busy, setBusy] = useState(false);
  const onFile = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    try { const { data } = await api.post("/media/upload", fd); onChange(data.url); toast.success("Avatar updated"); }
    catch (err) { toast.error(apiError(err, "Upload failed")); }
    finally { setBusy(false); e.target.value = ""; }
  };
  return (
    <div className="relative inline-block">
      <input type="file" accept="image/*" ref={ref} onChange={onFile} className="hidden" data-testid="avatar-upload-input" />
      <div className="h-20 w-20 rounded-full bg-[#1B2A4A] border border-white/10 mx-auto flex items-center justify-center font-heading text-3xl font-bold text-[#38bdf8] overflow-hidden">
        {value ? <img src={mediaSrc(value)} alt="" className="h-full w-full object-cover" /> : (name || "U").charAt(0)}
      </div>
      <button type="button" onClick={() => ref.current.click()} disabled={busy}
        className="absolute -bottom-1 -right-1 h-8 w-8 rounded-full bg-[#F26522] flex items-center justify-center text-white shadow-lg hover:bg-[#D95318]" title="Upload avatar" data-testid="avatar-upload-btn">
        {busy ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
      </button>
    </div>
  );
}

export function AttachmentUploader({ challengeId, attachments, onChange }) {
  const ref = useRef();
  const [busy, setBusy] = useState(false);

  const onFile = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/media/upload", fd);
      await api.post(`/admin/ctf/challenges/${challengeId}/attachments`, { media_id: data.id, filename: data.filename });
      toast.success("Attachment added");
      onChange();
    } catch (err) { toast.error(apiError(err, "Upload failed")); }
    finally { setBusy(false); e.target.value = ""; }
  };

  const remove = async (aid) => {
    try { await api.delete(`/admin/ctf/attachments/${aid}`); toast.success("Removed"); onChange(); }
    catch (err) { toast.error(apiError(err)); }
  };

  return (
    <div>
      <input type="file" ref={ref} onChange={onFile} className="hidden" data-testid="attachment-upload-input" />
      <div className="space-y-2">
        {(attachments || []).map((a) => (
          <div key={a.id} className="flex items-center gap-2 bg-[#0B1120] border border-white/5 rounded-lg px-3 py-2 text-sm" data-testid={`attachment-${a.id}`}>
            <FileText size={15} className="text-[#38bdf8] shrink-0" />
            <a href={mediaSrc(a.url) + "?download=1"} target="_blank" rel="noreferrer" className="flex-1 truncate hover:underline">{a.filename}</a>
            <span className="text-slate-500 text-xs">{Math.round((a.size_bytes || 0) / 1024)} KB</span>
            <button type="button" className="btn-ghost !px-1.5 text-red-400" onClick={() => remove(a.id)}><X size={14} /></button>
          </div>
        ))}
      </div>
      <button type="button" onClick={() => ref.current.click()} disabled={busy}
        className="btn-secondary text-sm mt-2" data-testid="add-attachment-btn">
        {busy ? <Loader2 size={14} className="animate-spin" /> : <Paperclip size={14} />} Add attachment
      </button>
    </div>
  );
}
